import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.js';
import { useAuth } from '../../auth/AuthProvider.js';

interface EmissionPoint {
  id: string;
  establishment_code: string;
  pos_code: string;
  label: string;
  address_street: string | null;
  address_city: string | null;
  is_default: boolean;
  is_active: boolean;
}

export function EmissionPointsPage() {
  const { t } = useTranslation('bookkeeping');
  const { activeOrgId } = useAuth();
  const qc = useQueryClient();
  const [form, setForm] = useState({
    establishment_code: '',
    pos_code: '',
    label: '',
    address_street: '',
    address_city: '',
  });
  const [error, setError] = useState<string | null>(null);

  const { data: list } = useQuery({
    queryKey: ['emission-points', activeOrgId],
    queryFn: () => api<EmissionPoint[]>('/bookkeeping/emission-points'),
    enabled: !!activeOrgId,
  });

  const createMut = useMutation({
    mutationFn: () =>
      api('/bookkeeping/emission-points', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(form),
      }),
    onSuccess: () => {
      setForm({
        establishment_code: '',
        pos_code: '',
        label: '',
        address_street: '',
        address_city: '',
      });
      setError(null);
      qc.invalidateQueries({ queryKey: ['emission-points'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const setDefaultMut = useMutation({
    mutationFn: (id: string) =>
      api(`/bookkeeping/emission-points/${id}/set-default`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['emission-points'] }),
  });

  const deactivateMut = useMutation({
    mutationFn: (id: string) => api(`/bookkeeping/emission-points/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['emission-points'] }),
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">{t('emissionPoint.title')}</h1>

      <table className="w-full rounded border bg-white text-sm">
        <thead>
          <tr className="border-b bg-slate-50 text-left">
            <th className="px-3 py-2">{t('emissionPoint.label')}</th>
            <th className="px-3 py-2">{t('emissionPoint.establishment_code')}</th>
            <th className="px-3 py-2">{t('emissionPoint.pos_code')}</th>
            <th className="px-3 py-2">{t('emissionPoint.address_street')}</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {(list ?? []).map((ep) => (
            <tr key={ep.id} className="border-b">
              <td className="px-3 py-2">
                {ep.label}
                {ep.is_default && (
                  <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-xs text-emerald-800">
                    {t('emissionPoint.default')}
                  </span>
                )}
              </td>
              <td className="px-3 py-2 font-mono">{ep.establishment_code}</td>
              <td className="px-3 py-2 font-mono">{ep.pos_code}</td>
              <td className="px-3 py-2">{ep.address_street ?? ''}</td>
              <td className="px-3 py-2 text-right">
                {!ep.is_default && (
                  <button
                    type="button"
                    onClick={() => setDefaultMut.mutate(ep.id)}
                    className="mr-2 text-xs text-blue-600 hover:underline"
                  >
                    {t('emissionPoint.set_default')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => deactivateMut.mutate(ep.id)}
                  className="text-xs text-red-600 hover:underline"
                >
                  {t('emissionPoint.deactivate')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="rounded border bg-white p-4">
        <h2 className="mb-3 text-sm font-semibold">{t('emissionPoint.create')}</h2>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <label>
            {t('emissionPoint.label')}
            <input
              type="text"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              className="mt-1 block w-full rounded border px-2 py-1"
            />
          </label>
          <label>
            {t('emissionPoint.establishment_code')}
            <input
              type="text"
              value={form.establishment_code}
              onChange={(e) => setForm({ ...form, establishment_code: e.target.value })}
              maxLength={3}
              className="mt-1 block w-full rounded border px-2 py-1 font-mono"
            />
          </label>
          <label>
            {t('emissionPoint.pos_code')}
            <input
              type="text"
              value={form.pos_code}
              onChange={(e) => setForm({ ...form, pos_code: e.target.value })}
              maxLength={3}
              className="mt-1 block w-full rounded border px-2 py-1 font-mono"
            />
          </label>
          <label>
            {t('emissionPoint.address_street')}
            <input
              type="text"
              value={form.address_street}
              onChange={(e) => setForm({ ...form, address_street: e.target.value })}
              className="mt-1 block w-full rounded border px-2 py-1"
            />
          </label>
        </div>
        {error && (
          <p className="mt-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
            {t(`emissionPoint.errors.${error}`, { defaultValue: error })}
          </p>
        )}
        <button
          type="button"
          onClick={() => createMut.mutate()}
          disabled={
            !form.label || !form.establishment_code || !form.pos_code || createMut.isPending
          }
          className="mt-3 rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:bg-slate-300"
        >
          {t('emissionPoint.create')}
        </button>
      </div>
    </div>
  );
}
